/**
 * Rich-text editor for a DocumentTemplate's `body` — the one field in the
 * composer (see DocumentTemplateList.tsx) that benefits from real WYSIWYG
 * formatting; `subject` and `forwardingAddressClause` stay plain text.
 *
 * Matches the controlled-component convention `TextInput`/`TextArea`
 * (components/Form.tsx) already establish (`value`/`onChange`), with one
 * real difference worth noting: Tiptap's `content` option only seeds the
 * *initial* document, it isn't continuously synced like a controlled
 * `<textarea>`'s `value` — harmless here since composing a template always
 * starts blank and the form is discarded after publish, never re-populated
 * mid-edit (there is no "load a past version back into the editor" flow).
 *
 * The toolbar's enabled marks/nodes are deliberately capped to exactly what
 * `apps/api/src/lib/html-sanitize.ts`'s allowlist permits and
 * `apps/api/src/lib/html-to-blocks.ts` knows how to render to a PDF — grow
 * one, grow all three.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Redo2,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import type { MergeTokenGroup } from 'shared';
import { MergeFieldNode } from './merge-field-node.js';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onEmptyChange?: (isEmpty: boolean) => void;
  tokenGroups: MergeTokenGroup[];
  /** Shown above the insert-field dropdown for a kind whose generator isn't merge-field-wired yet. */
  tokenGroupsNote?: string;
}

export function RichTextEditor({
  value,
  onChange,
  onEmptyChange,
  tokenGroups,
  tokenGroupsNote,
}: RichTextEditorProps) {
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  const fieldMenuRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        link: false,
        heading: { levels: [1, 2, 3] },
      }),
      MergeFieldNode,
    ],
    content: value,
    // v3 defaults to NOT re-rendering on every transaction (a perf change) —
    // this editor is small and the toolbar's isActive() checks need to
    // reflect the current selection on every keystroke/click, so opt back
    // into the always-rerender behavior.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: instance }) => {
      onChange(instance.getHTML());
      onEmptyChange?.(instance.isEmpty);
    },
  });

  // Same dropdown mechanics as SettingsPanel.tsx's gear icon, StaffLayout.tsx's
  // NavGroup, and TenantForm.tsx's lease-count pill — a fourth deliberate
  // copy rather than a shared hook extraction, matching this codebase's
  // existing convention for this exact pattern.
  useEffect(() => {
    if (!fieldMenuOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (fieldMenuRef.current && !fieldMenuRef.current.contains(event.target as Node))
        setFieldMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setFieldMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [fieldMenuOpen]);

  if (!editor) return null;

  function insertToken(token: string, label: string) {
    editor!.chain().focus().insertContent({ type: 'mergeField', attrs: { token, label } }).run();
    setFieldMenuOpen(false);
  }

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar">
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        >
          <Bold size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        >
          <Italic size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
        >
          <UnderlineIcon size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          label="Heading 1"
        >
          <Heading1 size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="Heading 2"
        >
          <Heading2 size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          label="Heading 3"
        >
          <Heading3 size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet list"
        >
          <List size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
        >
          <ListOrdered size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} label="Undo">
          <Undo2 size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} label="Redo">
          <Redo2 size={15} />
        </ToolbarButton>

        <div className="pill-menu" ref={fieldMenuRef}>
          <button
            type="button"
            className="rich-text-editor__toolbar-button pill-menu__trigger"
            aria-haspopup="true"
            aria-expanded={fieldMenuOpen}
            onClick={() => setFieldMenuOpen((v) => !v)}
          >
            Insert field ▾
          </button>
          {fieldMenuOpen ? (
            <div className="pill-menu__panel card rich-text-editor__field-panel">
              {tokenGroupsNote ? <p className="muted">{tokenGroupsNote}</p> : null}
              {tokenGroups.map((group) => (
                <div key={group.category}>
                  <p className="rich-text-editor__field-group-label muted">{group.category}</p>
                  {group.tokens.map((def) => (
                    <button
                      key={def.token}
                      type="button"
                      className="pill-menu__item rich-text-editor__field-item"
                      onClick={() => insertToken(def.token, def.label)}
                    >
                      {def.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <EditorContent editor={editor} className="rich-text-editor__content" />
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={
        active
          ? 'rich-text-editor__toolbar-button rich-text-editor__toolbar-button--active'
          : 'rich-text-editor__toolbar-button'
      }
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
    >
      {children}
    </button>
  );
}
